import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plane, Wallet, Users, ShieldCheck, ShieldAlert, ShieldX, Activity, Receipt,
  ArrowRight, PlusCircle, MessageSquareHeart, Loader2, MapPin, Building2,
  Crown, Star, TrendingUp, Lock, Video, BarChart3, Check,
} from "lucide-react";
import { toast } from "sonner";
import AccountSettings from "@/components/AccountSettings";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function Dashboard() {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
      <h1 className="font-heading text-2xl font-bold text-[#101828]">
        {user.role === "patient" ? "My journeys" : user.role === "provider" ? "Provider console" : "Admin console"}
      </h1>
      <p className="text-sm text-muted-foreground mt-1">Welcome back, {user.name}.</p>
      <div className="mt-6">
        {user.role === "patient" && <PatientDash />}
        {user.role === "provider" && <ProviderDash user={user} />}
        {user.role === "admin" && <AdminDash />}
      </div>
      <AccountSettings />
    </div>
  );
}

/* ---------------- Patient ---------------- */
function PatientDash() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/v1/itineraries").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);

  if (items === null) return <Loading />;
  if (items.length === 0)
    return (
      <div className="rounded-2xl border border-dashed border-[#0D5C75]/30 bg-[#E9F5F9] p-10 text-center">
        <MessageSquareHeart className="mx-auto text-[#0D5C75]" size={40} />
        <h3 className="font-heading font-bold text-lg mt-3 text-[#101828]">No journeys yet</h3>
        <p className="text-muted-foreground text-sm mt-1">Start by chatting with the AI assistant to find accredited care.</p>
        <div className="mt-5 flex justify-center gap-3">
          <Button className="rounded-full bg-[#0D5C75] text-white hover:bg-[#094A5E] hover:text-white" onClick={() => navigate("/assistant")} data-testid="empty-assistant-btn">Talk to assistant</Button>
          <Button variant="outline" className="rounded-full border-[#0D5C75]/30 text-[#0D5C75]" onClick={() => navigate("/book")} data-testid="empty-book-btn">Browse providers</Button>
        </div>
      </div>
    );

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
      {items.map((i) => (
        <div key={i.id} className="rounded-xl border border-[#E4E7EC] bg-white p-5 hover:-translate-y-1 transition-transform duration-200" data-testid={`journey-card-${i.id}`}>
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-heading font-bold text-[#101828]">{i.provider_name}</h3>
              <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin size={13} /> {i.package?.name}</p>
            </div>
            <Badge className={i.payment_status === "paid" ? "bg-[#12B76A] text-white border-0" : "bg-[#F79009] text-white border-0"}>
              {i.payment_status === "paid" ? "Paid" : "Unpaid"}
            </Badge>
          </div>
          <div className="mt-4 flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Total</span>
            <span className="font-heading font-bold text-[#0D5C75]">{money(i.split?.tcv)}</span>
          </div>
          <div className="mt-1 flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Escrow</span>
            <Badge variant="outline" className="border-[#0D5C75]/30 text-[#0D5C75]">{i.escrow_status}</Badge>
          </div>
          <Button className="mt-4 w-full rounded-full bg-[#0D5C75] text-white hover:bg-[#094A5E] hover:text-white"
            onClick={() => navigate(i.payment_status === "paid" ? `/journey/${i.id}` : "/book")} data-testid={`view-journey-${i.id}`}>
            {i.payment_status === "paid" ? "View journey" : "Complete payment"} <ArrowRight size={16} className="ml-1" />
          </Button>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Provider ---------------- */
function ProviderDash({ user }) {
  const navigate = useNavigate();
  const [provider, setProvider] = useState(undefined);
  const [bookings, setBookings] = useState([]);

  const load = () => {
    api.get("/v1/providers/mine").then((r) => setProvider(r.data)).catch(() => setProvider(null));
    api.get("/v1/itineraries").then((r) => setBookings(r.data)).catch(() => {});
  };
  useEffect(load, []);

  if (provider === undefined) return <Loading />;
  if (!provider) return <OnboardForm onDone={load} />;

  const stateBadge = {
    VERIFIED: <Badge className="bg-[#12B76A] text-white border-0"><ShieldCheck size={12} className="mr-1" />Verified</Badge>,
    PENDING: <Badge className="bg-[#F79009] text-white border-0"><ShieldAlert size={12} className="mr-1" />Pending review</Badge>,
    SUSPENDED: <Badge className="bg-destructive text-white border-0"><ShieldX size={12} className="mr-1" />Suspended</Badge>,
  }[provider.accreditation_state];

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-[#E4E7EC] bg-white p-6 flex flex-col sm:flex-row gap-5 items-start">
        <img src={provider.image} alt={provider.name} className="h-24 w-24 rounded-xl object-cover" />
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-heading text-xl font-bold text-[#101828]">{provider.name}</h2>
            {stateBadge}
            {provider.tier === "premium" && <Badge className="bg-[#F79009] text-white border-0"><Crown size={12} className="mr-1" />Premium Provider</Badge>}
            {provider.tier === "enterprise" && <Badge className="bg-[#0D5C75] text-white border-0"><Crown size={12} className="mr-1" />Enterprise Partner</Badge>}
          </div>
          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><MapPin size={13} /> {provider.city}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {(provider.accreditation || []).map((a) => <Badge key={a} variant="outline" className="border-[#0D5C75]/30 text-[#0D5C75]">{a}</Badge>)}
            {(provider.specialties || []).map((s) => <Badge key={s} variant="outline" className="border-[#E4E7EC] text-muted-foreground">{s}</Badge>)}
          </div>
        </div>
      </div>

      <Tabs defaultValue="console">
        <TabsList data-testid="provider-tabs">
          <TabsTrigger value="console" data-testid="tab-console">Console</TabsTrigger>
          <TabsTrigger value="growth" data-testid="tab-growth">Subscription &amp; Growth</TabsTrigger>
        </TabsList>

        <TabsContent value="console" className="space-y-6 mt-6">
          <div className="grid sm:grid-cols-3 gap-4">
            <StatCard icon={Activity} label="Clinical success" value={`${provider.success_rate}%`} />
            <StatCard icon={Users} label="Capacity" value={provider.capacity} />
            <StatCard icon={Receipt} label="Bookings" value={bookings.length} />
          </div>

          <div className="rounded-2xl border border-[#E4E7EC] bg-white p-6">
            <h3 className="font-heading font-bold text-[#101828] mb-4">Patient bookings</h3>
            {bookings.length === 0 ? <p className="text-sm text-muted-foreground">No bookings yet.</p> : (
              <div className="space-y-3">
                {bookings.map((b) => (
                  <div key={b.id} className="flex items-center justify-between rounded-xl border border-[#E4E7EC] p-4" data-testid={`booking-${b.id}`}>
                    <div>
                      <div className="font-medium text-[#101828]">{b.patient_name} · {b.package?.name}</div>
                      <div className="text-sm text-muted-foreground">{money(b.split?.tcv)} · <Badge variant="outline" className="border-[#0D5C75]/30 text-[#0D5C75]">{b.escrow_status}</Badge></div>
                    </div>
                    <Button variant="outline" className="border-[#0D5C75]/30 text-[#0D5C75] hover:bg-[#E9F5F9]"
                      onClick={() => navigate(`/journey/${b.id}`)} data-testid={`manage-booking-${b.id}`}>Manage <ArrowRight size={15} className="ml-1" /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="growth" className="mt-6">
          <SubscriptionPanel bookings={bookings} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- Provider · Subscription & Growth ---------------- */
const TIER_META = {
  basic: { icon: Star, color: "#667085" },
  premium: { icon: Crown, color: "#F79009" },
  enterprise: { icon: Crown, color: "#0D5C75" },
};

function SubscriptionPanel({ bookings }) {
  const [tiers, setTiers] = useState([]);
  const [mine, setMine] = useState(null);
  const [busy, setBusy] = useState("");
  const [featured, setFeatured] = useState(false);

  const load = () => {
    api.get("/v1/subscriptions/tiers").then((r) => setTiers(r.data)).catch(() => {});
    api.get("/v1/subscriptions/mine").then((r) => setMine(r.data)).catch(() => setMine({ tier: "basic" }));
    api.get("/v1/providers/mine").then((r) => setFeatured(!!r.data.featured)).catch(() => {});
  };
  useEffect(load, []);

  const subscribe = async (tier) => {
    setBusy(tier);
    try { await api.post("/v1/subscriptions/subscribe", { tier }); toast.success(`Subscribed to ${tier}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not subscribe"); } finally { setBusy(""); }
  };

  const buyFeatured = async () => {
    setBusy("featured");
    try { await api.post("/v1/featured/purchase"); toast.success("Featured listing active for 7 days"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not purchase"); } finally { setBusy(""); }
  };

  const currentTier = mine?.tier || "basic";
  const paidBookings = bookings.filter((b) => b.payment_status === "paid");
  const revenue = paidBookings.reduce((s, b) => s + (b.split?.tcv || 0), 0);
  const isPremium = currentTier === "premium" || currentTier === "enterprise";
  const isEnterprise = currentTier === "enterprise";

  if (!mine) return <Loading />;

  return (
    <div className="space-y-6">
      {/* Tier cards */}
      <div className="grid md:grid-cols-3 gap-4" data-testid="subscription-tiers">
        {tiers.map((t) => {
          const M = TIER_META[t.tier] || TIER_META.basic;
          const active = currentTier === t.tier;
          return (
            <div key={t.tier} className={`rounded-2xl border p-6 flex flex-col ${active ? "border-[#0D5C75] ring-1 ring-[#0D5C75] bg-[#E9F5F9]" : "border-[#E4E7EC] bg-white"}`} data-testid={`tier-card-${t.tier}`}>
              <div className="flex items-center gap-2">
                <M.icon size={18} style={{ color: M.color }} />
                <h4 className="font-heading font-bold text-[#101828] capitalize">{t.tier}</h4>
                {active && <Badge className="bg-[#12B76A] text-white border-0 ml-auto">Current</Badge>}
              </div>
              <div className="mt-3 font-heading text-2xl font-extrabold text-[#101828]">
                {t.price_monthly === 0 ? "Free" : money(t.price_monthly)}<span className="text-sm font-normal text-muted-foreground">{t.price_monthly === 0 ? "" : "/mo"}</span>
              </div>
              <ul className="mt-4 space-y-2 flex-1">
                {t.features.map((f) => <li key={f} className="text-sm text-[#475467] flex items-start gap-2"><Check size={15} className="mt-0.5 text-[#12B76A] shrink-0" />{f}</li>)}
              </ul>
              <Button disabled={active || busy === t.tier} onClick={() => subscribe(t.tier)}
                className={`mt-5 rounded-full ${active ? "bg-[#E4E7EC] text-muted-foreground" : "bg-[#0D5C75] text-white hover:bg-[#094A5E] hover:text-white"}`}
                data-testid={`subscribe-${t.tier}`}>
                {busy === t.tier ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                {active ? "Active plan" : t.price_monthly === 0 ? "Downgrade" : `Upgrade · ${money(t.price_monthly)}/mo`}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Featured listing */}
      <div className="rounded-2xl border border-[#E4E7EC] bg-white p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="grid place-items-center h-10 w-10 rounded-lg bg-[#FFF4ED] text-[#EA580C]"><TrendingUp size={20} /></span>
          <div>
            <div className="font-heading font-bold text-[#101828]">Featured Listing {featured && <Badge className="bg-[#EA580C] text-white border-0 ml-1">Active</Badge>}</div>
            <p className="text-sm text-muted-foreground mt-0.5 max-w-md">Pin your facility to the top of search results with an orange "Featured" badge for 7 days.</p>
          </div>
        </div>
        <div className="text-right">
          <div className="font-heading font-bold text-[#0D5C75]">$49<span className="text-sm font-normal text-muted-foreground">/week</span></div>
          <Button disabled={busy === "featured"} onClick={buyFeatured}
            className="mt-2 rounded-full bg-[#EA580C] text-white hover:bg-[#c2410c] hover:text-white" data-testid="buy-featured-btn">
            {busy === "featured" ? <Loader2 className="animate-spin mr-2" size={16} /> : null}{featured ? "Extend" : "Purchase"}
          </Button>
        </div>
      </div>

      {/* Analytics (tier-gated) */}
      <div className="rounded-2xl border border-[#E4E7EC] bg-white p-6">
        <div className="flex items-center gap-2 mb-4"><BarChart3 size={18} className="text-[#0D5C75]" /><h3 className="font-heading font-bold text-[#101828]">Growth analytics</h3></div>
        <div className="grid sm:grid-cols-3 gap-4">
          <StatCard icon={Users} label="Total bookings" value={bookings.length} />
          <div className="relative">
            <StatCard icon={Receipt} label="Inquiries / conversion" value={isPremium ? `${paidBookings.length}/${bookings.length || 0}` : "—"} />
            {!isPremium && <LockOverlay tier="Premium" />}
          </div>
          <div className="relative">
            <StatCard icon={Wallet} label="Revenue (TCV)" value={isPremium ? money(revenue) : "—"} />
            {!isPremium && <LockOverlay tier="Premium" />}
          </div>
        </div>
        <div className="mt-4 relative rounded-xl border border-[#E4E7EC] p-5">
          <div className="text-sm font-medium text-[#101828] mb-1">Advanced analytics · demographics, trends &amp; benchmarking</div>
          <p className="text-sm text-muted-foreground">Patient origin breakdown, month-over-month booking trends and peer benchmarking against similar facilities.</p>
          {!isEnterprise && <LockOverlay tier="Enterprise" />}
        </div>
      </div>
    </div>
  );
}

function LockOverlay({ tier }) {
  return (
    <div className="absolute inset-0 rounded-xl bg-white/70 backdrop-blur-sm grid place-items-center text-center px-3">
      <div className="flex flex-col items-center gap-1">
        <Lock size={18} className="text-[#0D5C75]" />
        <span className="text-xs font-medium text-[#101828]">Unlock with {tier}</span>
      </div>
    </div>
  );
}

function OnboardForm({ onDone }) {
  const [form, setForm] = useState({ name: "", city: "", specialties: "Cardiology", accreditation: "NABH", about: "", success_rate: 85, capacity: 5, pkgName: "", pkgPrice: 5000, pkgDays: 10 });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/v1/providers/onboard", {
        name: form.name, city: form.city,
        specialties: form.specialties.split(",").map((s) => s.trim()),
        accreditation: form.accreditation.split(",").map((s) => s.trim()),
        about: form.about, success_rate: Number(form.success_rate), capacity: Number(form.capacity),
        packages: form.pkgName ? [{ name: form.pkgName, specialty: form.specialties.split(",")[0].trim(), price: Number(form.pkgPrice), duration_days: Number(form.pkgDays) }] : [],
      });
      toast.success("Submitted for accreditation review");
      onDone();
    } catch { toast.error("Could not submit"); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-[#E4E7EC] bg-white p-6 max-w-2xl">
      <div className="flex items-center gap-2 mb-1"><Building2 className="text-[#0D5C75]" size={20} /><h2 className="font-heading text-lg font-bold text-[#101828]">Onboard your facility</h2></div>
      <p className="text-sm text-muted-foreground mb-5">Submit details for NABH/JCI accreditation review by our admin team.</p>
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
        <Field label="Facility name" v={form.name} onChange={set("name")} testid="onboard-name" required />
        <Field label="City" v={form.city} onChange={set("city")} testid="onboard-city" required />
        <Field label="Specialties (comma-sep)" v={form.specialties} onChange={set("specialties")} testid="onboard-specialties" />
        <Field label="Accreditation (comma-sep)" v={form.accreditation} onChange={set("accreditation")} testid="onboard-accreditation" />
        <Field label="Success rate %" type="number" v={form.success_rate} onChange={set("success_rate")} testid="onboard-success" />
        <Field label="Capacity" type="number" v={form.capacity} onChange={set("capacity")} testid="onboard-capacity" />
        <div className="sm:col-span-2">
          <Label>About</Label>
          <Textarea value={form.about} onChange={set("about")} className="mt-1.5" data-testid="onboard-about" />
        </div>
        <Field label="Package name" v={form.pkgName} onChange={set("pkgName")} testid="onboard-pkg-name" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Price $" type="number" v={form.pkgPrice} onChange={set("pkgPrice")} testid="onboard-pkg-price" />
          <Field label="Days" type="number" v={form.pkgDays} onChange={set("pkgDays")} testid="onboard-pkg-days" />
        </div>
        <div className="sm:col-span-2">
          <Button type="submit" disabled={saving} className="rounded-full bg-[#0D5C75] text-white hover:bg-[#094A5E] hover:text-white" data-testid="onboard-submit-btn">
            {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <PlusCircle size={16} className="mr-1.5" />} Submit for review
          </Button>
        </div>
      </form>
    </div>
  );
}

/* ---------------- Admin ---------------- */
function AdminDash() {
  const [ov, setOv] = useState(null);
  const [providers, setProviders] = useState([]);
  const [ledger, setLedger] = useState([]);

  const load = () => {
    api.get("/v1/admin/overview").then((r) => setOv(r.data)).catch(() => {});
    api.get("/v1/admin/providers").then((r) => setProviders(r.data)).catch(() => {});
    api.get("/v1/admin/ledger").then((r) => setLedger(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const verify = async (id, state) => {
    try { await api.post(`/v1/admin/providers/${id}/verify`, { state }); toast.success(`Marked ${state}`); load(); }
    catch { toast.error("Failed"); }
  };

  if (!ov) return <Loading />;

  return (
    <Tabs defaultValue="ops">
      <TabsList data-testid="admin-tabs">
        <TabsTrigger value="ops" data-testid="tab-ops">Operations</TabsTrigger>
        <TabsTrigger value="revenue" data-testid="tab-revenue">Revenue</TabsTrigger>
      </TabsList>

      <TabsContent value="ops" className="space-y-6 mt-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <StatCard icon={Wallet} label="Funds in escrow" value={money(ov.funds_in_escrow)} highlight />
          <StatCard icon={Receipt} label="Platform fees" value={money(ov.platform_fees)} />
          <StatCard icon={Plane} label="Paid bookings" value={ov.paid_bookings} />
          <StatCard icon={Users} label="Total users" value={ov.total_users} />
        </div>

        <div className="rounded-2xl border border-[#E4E7EC] bg-white p-6">
          <div className="flex items-center gap-2 mb-4"><ShieldCheck size={18} className="text-[#0D5C75]" /><h3 className="font-heading font-bold text-[#101828]">Accreditation verification</h3>
            {ov.pending_verifications > 0 && <Badge className="bg-[#F79009] text-white border-0">{ov.pending_verifications} pending</Badge>}
          </div>
          <div className="space-y-3">
            {providers.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#E4E7EC] p-4" data-testid={`admin-provider-${p.id}`}>
                <div>
                  <div className="font-medium text-[#101828]">{p.name} <span className="text-sm text-muted-foreground">· {p.city}</span></div>
                  <div className="text-sm text-muted-foreground">{(p.accreditation || []).join(", ")} · {p.accreditation_state}</div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-[#12B76A] text-white hover:bg-[#0e9a59] hover:text-white" onClick={() => verify(p.id, "VERIFIED")} data-testid={`verify-${p.id}`}>Verify</Button>
                  <Button size="sm" variant="outline" className="border-destructive/40 text-destructive hover:bg-destructive/5" onClick={() => verify(p.id, "SUSPENDED")} data-testid={`suspend-${p.id}`}>Suspend</Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-[#E4E7EC] bg-white p-6">
          <div className="flex items-center gap-2 mb-4"><Wallet size={18} className="text-[#0D5C75]" /><h3 className="font-heading font-bold text-[#101828]">Escrow ledger</h3></div>
          {ledger.length === 0 ? <p className="text-sm text-muted-foreground">No funded bookings yet.</p> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Patient</TableHead><TableHead>Provider</TableHead><TableHead>Status</TableHead>
                  <TableHead className="text-right">TCV</TableHead><TableHead className="text-right">Released</TableHead><TableHead className="text-right">Held</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ledger.map((r) => (
                  <TableRow key={r.itinerary_id} data-testid={`ledger-row-${r.itinerary_id}`}>
                    <TableCell>{r.patient_name}</TableCell>
                    <TableCell>{r.provider_name}</TableCell>
                    <TableCell><Badge variant="outline" className="border-[#0D5C75]/30 text-[#0D5C75]">{r.escrow_status}</Badge></TableCell>
                    <TableCell className="text-right">{money(r.tcv)}</TableCell>
                    <TableCell className="text-right text-[#12B76A]">{money(r.released)}</TableCell>
                    <TableCell className="text-right text-[#F79009]">{money(r.held)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </TabsContent>

      <TabsContent value="revenue" className="mt-6">
        <RevenuePanel />
      </TabsContent>
    </Tabs>
  );
}

/* ---------------- Admin · Revenue ---------------- */
const STREAM_LABELS = {
  medical_commissions: "Medical commissions (15%)",
  accommodation_commissions: "Accommodation commissions (12%)",
  travel_commissions: "Travel affiliate (3%)",
  insurance_commissions: "Travel insurance (15%)",
  subscription_revenue: "Provider subscriptions",
  featured_revenue: "Featured listings",
  concierge_revenue: "Concierge packages",
  teleconsultation_revenue: "Teleconsultations (20%)",
};

function RevenuePanel() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/v1/admin/revenue").then((r) => setData(r.data)).catch(() => setData(null)); }, []);
  if (!data) return <Loading />;

  const maxTrend = Math.max(...data.trend_values, 1);
  const maxStream = Math.max(...Object.values(data.streams), 1);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <StatCard icon={TrendingUp} label="Total platform revenue" value={money(data.total_revenue)} highlight />
        <StatCard icon={Crown} label="Premium providers" value={data.tier_counts.premium} />
        <StatCard icon={Building2} label="Enterprise partners" value={data.tier_counts.enterprise} />
        <StatCard icon={Star} label="Basic providers" value={data.tier_counts.basic} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Revenue streams */}
        <div className="rounded-2xl border border-[#E4E7EC] bg-white p-6">
          <div className="flex items-center gap-2 mb-5"><Wallet size={18} className="text-[#0D5C75]" /><h3 className="font-heading font-bold text-[#101828]">Revenue by stream</h3></div>
          <div className="space-y-3">
            {Object.entries(data.streams).map(([k, v]) => (
              <div key={k} data-testid={`stream-${k}`}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">{STREAM_LABELS[k] || k}</span>
                  <span className="font-medium text-[#101828]">{money(v)}</span>
                </div>
                <div className="h-2 rounded-full bg-[#F2F4F7] overflow-hidden">
                  <div className="h-full rounded-full bg-[#0D5C75]" style={{ width: `${(v / maxStream) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Trend */}
        <div className="rounded-2xl border border-[#E4E7EC] bg-white p-6">
          <div className="flex items-center gap-2 mb-5"><BarChart3 size={18} className="text-[#0D5C75]" /><h3 className="font-heading font-bold text-[#101828]">6-month revenue trend</h3></div>
          <div className="flex items-end justify-between gap-2 h-40">
            {data.trend_values.map((v, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-2" data-testid={`trend-bar-${i}`}>
                <div className="w-full rounded-t-md bg-[#2A9D8F] transition-all" style={{ height: `${(v / maxTrend) * 100}%`, minHeight: 4 }} title={money(v)} />
                <span className="text-xs text-muted-foreground">{data.trend_months[i]}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top providers */}
      <div className="rounded-2xl border border-[#E4E7EC] bg-white p-6">
        <h3 className="font-heading font-bold text-[#101828] mb-4">Top providers by contract value</h3>
        {data.top_providers.length === 0 ? <p className="text-sm text-muted-foreground">No paid bookings yet.</p> : (
          <div className="space-y-2">
            {data.top_providers.map((p, i) => (
              <div key={i} className="flex items-center justify-between rounded-xl border border-[#E4E7EC] p-3" data-testid={`top-provider-${i}`}>
                <span className="flex items-center gap-2 text-[#101828]"><span className="grid place-items-center h-6 w-6 rounded-full bg-[#E9F5F9] text-[#0D5C75] text-xs font-bold">{i + 1}</span>{p.provider}</span>
                <span className="font-heading font-bold text-[#0D5C75]">{money(p.revenue)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- shared ---------------- */
function Loading() { return <div className="py-16 grid place-items-center text-muted-foreground"><Loader2 className="animate-spin" /></div>; }

function StatCard({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`rounded-xl border p-5 ${highlight ? "border-[#0D5C75] bg-[#E9F5F9]" : "border-[#E4E7EC] bg-white"}`}>
      <Icon size={20} className="text-[#0D5C75]" />
      <div className="font-heading text-2xl font-extrabold text-[#101828] mt-3">{value}</div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </div>
  );
}

function Field({ label, v, onChange, type = "text", testid, required }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input type={type} value={v} onChange={onChange} required={required} className="mt-1.5 focus:ring-2 focus:ring-[#2A9D8F]" data-testid={testid} />
    </div>
  );
}
